/**
 * Translation types for frontend
 * Matches backend translation structure
 */
export type SupportedLanguage = string;

export interface Translations {
  [fieldName: string]: {
    [languageCode: string]: string | undefined;
  };
}

export interface TranslatableEntity {
  translations?: Translations;
}

/**
 * Get translated value for a field, falling back to original if translation doesn't exist
 */
export function getTranslatedValue<T extends TranslatableEntity>(
  entity: T,
  fieldName: keyof T,
  language: SupportedLanguage = "en"
): string {
  // English is always the original field value
  if (language === "en") {
    const value = (entity as any)[fieldName];
    return typeof value === "string" ? value : "";
  }

  // For other languages, check translations
  const translations = entity.translations?.[fieldName as string];
  if (translations && translations[language]) {
    return translations[language] || "";
  }

  // Fallback to English (original field)
  const originalValue = (entity as any)[fieldName];
  return typeof originalValue === "string" ? originalValue : "";
}

/**
 * Get all translations for a field
 */
export function getFieldTranslations<T extends TranslatableEntity>(
  entity: T,
  fieldName: keyof T
): Record<SupportedLanguage, string> {
  const result: Record<string, string> = {
    en: typeof (entity as any)[fieldName] === "string" ? (entity as any)[fieldName] : "",
  };

  const translations = entity.translations?.[fieldName as string];
  if (translations) {
    (["de", "ar"] as SupportedLanguage[]).forEach((lang) => {
      if (translations[lang]) {
        result[lang] = translations[lang] || "";
      }
    });
  }

  // Fill in missing languages with empty strings
  (["en", "de", "ar"] as SupportedLanguage[]).forEach((lang) => {
    if (!result[lang]) {
      result[lang] = "";
    }
  });

  return result as Record<SupportedLanguage, string>;
}

